/**
 * Visual primitives shared across the detail tab panels — a metric "tile"
 * and a section header. Keeps each tab panel small & focused on its math.
 */
import { cn } from "@/lib/utils";

interface MetricTileProps {
  label: string;
  value: string;
  sub?: string;
  /** "pos" tints green · "neg" tints red · "dim" stays neutral */
  tone?: "pos" | "neg" | "dim";
  /** Span 2 columns in the parent grid. */
  wide?: boolean;
  children?: React.ReactNode;
}

export function MetricTile({
  label,
  value,
  sub,
  tone = "dim",
  wide,
  children,
}: MetricTileProps) {
  return (
    <div
      className={cn(
        "rounded-md border border-border bg-muted/40 p-3",
        wide && "sm:col-span-2",
      )}
    >
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div
        className={cn(
          "text-lg font-extrabold leading-tight",
          tone === "pos" && "text-success",
          tone === "neg" && "text-destructive",
        )}
      >
        {value}
      </div>
      {sub && (
        <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>
      )}
      {children}
    </div>
  );
}

interface SectionHeaderProps {
  Icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}

export function SectionHeader({ Icon, children }: SectionHeaderProps) {
  return (
    <div className="mt-4 mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary first:mt-0">
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {children}
    </div>
  );
}

/** Two-tone label badge (matches the cf-pct hint from the mockup). */
export function HintBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="ml-1 text-[10px] font-medium text-muted-foreground">
      {children}
    </span>
  );
}
